import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Trans, useTranslation } from 'react-i18next';
import { FaCalendarAlt, FaArrowRight } from 'react-icons/fa';
import { publicAssetUrl } from '../../utils/publicAssetUrl';

const BRAND_RED = '#D52B1E';

const Wrap = styled(motion.div)`
  flex: 0 1 auto;
  width: 100%;
  max-width: ${(p) => (p.$fullBleed ? '100%' : 'min(100%, 960px)')};
  margin: 0 auto 2rem;
  align-self: center;
  box-sizing: border-box;
`;

const Poster = styled.div`
  background: linear-gradient(165deg, #0a1628 0%, #1e293b 45%, #0f172a 100%);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.06);
`;

const Hero = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 21 / 9;
  min-height: 140px;
  max-height: 320px;
  background: linear-gradient(135deg, #043663 0%, #0a5a9e 50%, #1e3a5f 100%);
  ${(p) =>
    p.$img
      ? `
    background-image: url(${p.$img});
    background-size: cover;
    background-position: center;
  `
      : ''}
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(15, 23, 42, 0.92) 0%, rgba(15, 23, 42, 0.35) 45%, transparent 100%);
  }
`;

const LogoRow = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const TenantLogo = styled.img`
  width: 56px;
  height: 56px;
  object-fit: contain;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.95);
  padding: 6px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
`;

const Body = styled.div`
  padding: 1.5rem clamp(1rem, 3vw, 1.75rem) 1.75rem;
  position: relative;
  margin-top: -2.5rem;
  z-index: 2;
`;

const Kicker = styled.p`
  margin: 0 0 0.35rem 0;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #94a3b8;
`;

const Title = styled.h2`
  margin: 0 0 0.5rem 0;
  font-size: clamp(1.35rem, 3.5vw, 1.85rem);
  font-weight: 800;
  line-height: 1.15;
  color: #f8fafc;
  letter-spacing: -0.02em;
`;

const SeasonLine = styled.p`
  margin: 0 0 0.85rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: #cbd5e1;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 1.5rem;
  margin: 0 0 1.1rem;
  font-size: 0.82rem;
  color: #94a3b8;
  align-items: center;
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
`;

const YourTeamsPanel = styled.div`
  margin: 0 0 1.25rem;
  padding: 1rem 1.15rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const YourTeamsTitle = styled.div`
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 0.65rem;
`;

const TeamDashLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.55rem 0.65rem;
  margin-bottom: 0.4rem;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.45);
  color: #e2e8f0;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  &:last-child {
    margin-bottom: 0;
  }
  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
    color: #fff;
  }
`;

const FeeBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  margin: 0 0 1.25rem;
  padding: 1rem 1.25rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const FeeLabel = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #94a3b8;
`;

const FeeAmount = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: #fbbf24;
  line-height: 1.1;
  text-shadow: 0 2px 20px rgba(251, 191, 36, 0.25);
`;

const FeeHint = styled.p`
  margin: 0.5rem 0 0 0;
  font-size: 0.8125rem;
  color: #cbd5e1;
  line-height: 1.45;
`;

const Desc = styled.div`
  font-size: 0.9375rem;
  line-height: 1.6;
  color: #cbd5e1;
  margin-bottom: 1rem;
  white-space: pre-wrap;
`;

const ChoiceHint = styled.p`
  margin: 0 0 1rem;
  font-size: 0.9rem;
  line-height: 1.55;
  color: #cbd5e1;
  text-align: center;
  max-width: 36rem;
`;

const CenterStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 0.75rem;
  margin-top: 0.25rem;
`;

const CtaNarrow = styled.div`
  width: 100%;
  max-width: 420px;
`;

const BtnRegister = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.9rem 1.35rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  color: #fff;
  background: ${BRAND_RED};
  box-shadow: 0 10px 28px rgba(213, 43, 30, 0.35);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 32px rgba(213, 43, 30, 0.45);
    color: #fff;
  }
`;

const TeamSelectBlock = styled.div`
  width: 100%;
`;

const TeamSelect = styled.select`
  width: 100%;
  padding: 0.55rem 0.75rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(15, 23, 42, 0.6);
  color: #f1f5f9;
  font-size: 0.95rem;
  margin-bottom: 0.75rem;
`;

const BtnSecondary = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.08);
  color: #e2e8f0;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.12);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const NewTeamLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.8rem 1.25rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.95rem;
  border: 1px solid rgba(251, 191, 36, 0.45);
  background: rgba(251, 191, 36, 0.1);
  color: #fcd34d;
  text-decoration: none;
  text-align: center;
  margin-bottom: 0.65rem;
  transition: background 0.15s ease, border-color 0.15s ease;
  &:hover {
    background: rgba(251, 191, 36, 0.18);
    border-color: rgba(251, 191, 36, 0.65);
    color: #fef3c7;
  }
`;

const OrDivider = styled.p`
  margin: 0 0 0.75rem 0;
  font-size: 0.78rem;
  text-align: center;
  color: #64748b;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const PartnerBox = styled.div`
  margin-top: 0.75rem;
  padding: 0.85rem 0.9rem;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.55);
  border: 1px solid rgba(148, 163, 184, 0.25);
  text-align: left;
`;

const PartnerTitle = styled.div`
  font-size: 0.78rem;
  font-weight: 700;
  color: #e2e8f0;
  margin-bottom: 0.45rem;
`;

const PartnerList = styled.ul`
  margin: 0 0 0.65rem;
  padding: 0 0 0 1rem;
  color: #94a3b8;
  font-size: 0.8rem;
  line-height: 1.45;
`;

const RadioRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
  font-size: 0.8rem;
  color: #cbd5e1;
  margin-bottom: 0.4rem;
  cursor: pointer;
  input {
    margin-top: 0.15rem;
  }
`;

const Subtle = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.72rem;
  color: #64748b;
  line-height: 1.4;
`;

function formatMonthYear(d) {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  } catch {
    return null;
  }
}

/**
 * Full-width campaign card for registration: banner + description + Create a team.
 * Continue / partner choices live on /player/register-team.
 */
export default function CampaignPosterView({
  campaign,
  fullBleed = false,
  tenantName,
  seasonName,
  registrationChoiceFooter,
  registerHref,
  campaignTeamEligible,
}) {
  const { t } = useTranslation();
  const pt = 'playerTournamentsPage';
  const bannerSrc = campaign?.banner_display_url ? publicAssetUrl(campaign.banner_display_url) : '';
  const logoSrc = campaign?.tenant_logo_url ? publicAssetUrl(campaign.tenant_logo_url) : '';

  const starts = formatMonthYear(campaign?.start_date);

  return (
    <Wrap
      $fullBleed={fullBleed}
      initial={{ opacity: 0, y: -36 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      <Poster>
        <Hero $img={bannerSrc || undefined}>
          {logoSrc ? (
            <LogoRow>
              <TenantLogo src={logoSrc} alt="" />
            </LogoRow>
          ) : null}
        </Hero>
        <Body>
          <Kicker>{t(`${pt}.currentEditionKicker`, { league: tenantName || 'League' })}</Kicker>
          <Title>{campaign?.title || t(`${pt}.editionFallback`)}</Title>
          {seasonName ? <SeasonLine>{seasonName}</SeasonLine> : null}
          {starts ? (
            <MetaRow>
              <MetaItem>
                <FaCalendarAlt aria-hidden />
                Starts {starts}
              </MetaItem>
            </MetaRow>
          ) : null}

          {campaign?.description ? <Desc>{campaign.description}</Desc> : null}

          {registrationChoiceFooter ? (
            <>
              <ChoiceHint>
                {seasonName ? (
                  <Trans
                    i18nKey={`${pt}.posterChoiceWithSeason`}
                    values={{ season: seasonName }}
                    components={{
                      0: <strong style={{ color: '#f1f5f9' }} />,
                      1: <strong />,
                      2: <strong />,
                    }}
                  />
                ) : (
                  <Trans i18nKey={`${pt}.posterChoiceNoSeason`} components={{ 0: <strong /> }} />
                )}
              </ChoiceHint>
              <CenterStack>{registrationChoiceFooter}</CenterStack>
            </>
          ) : null}

          {!registrationChoiceFooter && !campaignTeamEligible && registerHref ? (
            <CenterStack>
              <CtaNarrow>
                <BtnRegister to={registerHref}>
                  {t(`${pt}.createNewTeamShort`, 'Create a team')}{' '}
                  <FaArrowRight size={14} aria-hidden />
                </BtnRegister>
              </CtaNarrow>
            </CenterStack>
          ) : null}

          {!registrationChoiceFooter && campaignTeamEligible ? (
            <CenterStack>
              <p
                style={{
                  margin: 0,
                  color: '#94a3b8',
                  fontSize: '0.9rem',
                  textAlign: 'center',
                  maxWidth: '28rem',
                  lineHeight: 1.5,
                }}
              >
                <Trans i18nKey={`${pt}.posterTeamRegistered`} components={{ 0: <strong /> }} />
              </p>
              {registerHref ? (
                <CtaNarrow style={{ marginTop: '1rem' }}>
                  <BtnRegister to={registerHref}>
                    {t(`${pt}.createNewTeamShort`, 'Create a team')}{' '}
                    <FaArrowRight size={14} aria-hidden />
                  </BtnRegister>
                </CtaNarrow>
              ) : null}
            </CenterStack>
          ) : null}
        </Body>
      </Poster>
    </Wrap>
  );
}

